'use client'

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { FIELDNAMES, KNOWN_FORM_VALUES } from '../Form/utils/constants'

const {
  CUSTOM_DONATION_AMOUNT,
  ONE_TIME_DONATION_FREQUENCY,
  GENERIC_DONATION_TYPE,
} = KNOWN_FORM_VALUES
const {
  DONATION_FREQUENCY,
  DONATION_TYPE,
  DONATION_PAYMENT_METHOD_ON_RECURRENCE_PAYMENT_TYPE,
} = FIELDNAMES

const RadioGroup = ({
  label = 'Ricorrenza della donazione',
  classNames = {
    radioGroup: 'radio-group',
    radioOption: 'radio-option',
    radioOptionLabel: 'radio-option_label',
    radioOptionSubLabel: 'radio-option_sublabel',
  },
  name = 'donation-period',
  defaultValue: _defaultValue = '',
  radioOptions: _radioOptions = [],
  radioOptionsByField = false,
  isHidden = false,
  value = '',
  useFormContextIfAvailable = false,
}) => {
  const { register, watch, setValue, getValues, setError, clearErrors } =
    useFormContext()

  const watchedDonationFrequency = watch(DONATION_FREQUENCY)
  const watchedDonationType = watch(DONATION_TYPE)
  const watchedRecurrencePaymentType = watch(
    DONATION_PAYMENT_METHOD_ON_RECURRENCE_PAYMENT_TYPE
  )
  const watchedFieldValue = watch(name)

  const formValues = getValues()

  const [radioOptions, defaultValue] = useMemo(
    () =>
      useFormContextIfAvailable
        ? getRadioOptions(
            _radioOptions,
            radioOptionsByField,
            formValues,
            _defaultValue,
            value
          )
        : [_radioOptions, _defaultValue],
    [
      _radioOptions,
      radioOptionsByField,
      formValues,
      _defaultValue,
      value,
      useFormContextIfAvailable,
    ]
  )

  const isEditField = name.includes('edit')
  const currentFormValue = watchedFieldValue || formValues[name] || value || ''

  const [selectedRadioValue, setSelectedRadioValue] = useState(
    useFormContextIfAvailable && !isEditField ? currentFormValue : defaultValue
  )

  const [radioInputValue, updateRadioInputValue] = useState(
    selectedRadioValue.startsWith(`${CUSTOM_DONATION_AMOUNT}-`)
      ? selectedRadioValue.split('-')[2]
      : ''
  )

  const [lastCustomValue, setLastCustomValue] = useState(
    selectedRadioValue.startsWith(`${CUSTOM_DONATION_AMOUNT}-`)
      ? selectedRadioValue.split('-')[2]
      : ''
  )

  const customOptionType = 'number'
  const currentFormSelection = currentFormValue || selectedRadioValue
  const { onChange, ref } = register(name)
  const donationFrequency = watchedDonationFrequency

  const minimumValue = useMemo(() => {
    if (!useFormContextIfAvailable) return 1

    if (donationFrequency === 'monthly' || donationFrequency === 'yearly') {
      const numericValues = radioOptions
        .filter((option) => !option.value.includes(CUSTOM_DONATION_AMOUNT))
        .map((option) => Number(option.value))
        .filter((value) => !isNaN(value))

      return numericValues.length > 0 ? Math.min(...numericValues) : 1
    }

    return 1
  }, [radioOptions, donationFrequency, useFormContextIfAvailable])

  // Memoizza funzioni per evitare ricreazione
  const isRadioButtonChecked = useCallback(
    (value, index = null) => {
      if (
        selectedRadioValue.startsWith(`${CUSTOM_DONATION_AMOUNT}-`) ||
        currentFormSelection.startsWith(`${CUSTOM_DONATION_AMOUNT}-`)
      ) {
        return value.startsWith(CUSTOM_DONATION_AMOUNT)
      }

      if (selectedRadioValue === '' || currentFormSelection === '') {
        return false
      }

      const currentRadioOptionsHasValue = radioOptions.some(
        (option) => option.value === value
      )
      const currentRadioOptionsHasSelectedOrCurrentFormValue =
        radioOptions.some(
          (option) =>
            option.value === selectedRadioValue ||
            option.value === currentFormValue
        )

      if (
        !currentRadioOptionsHasSelectedOrCurrentFormValue &&
        currentRadioOptionsHasValue &&
        !!defaultValue
      ) {
        return index === 0
      }
      if (
        defaultValue !== '' &&
        (selectedRadioValue === defaultValue ||
          currentFormValue === defaultValue)
      ) {
        return value === defaultValue
      }
      return value === selectedRadioValue || value === currentFormSelection
    },
    [
      selectedRadioValue,
      currentFormSelection,
      radioOptions,
      currentFormValue,
      defaultValue,
    ]
  )

  const addCustomValue = useCallback(
    (value) => {
      if (value?.startsWith(CUSTOM_DONATION_AMOUNT)) {
        if (selectedRadioValue.startsWith(`${CUSTOM_DONATION_AMOUNT}-`)) {
          return selectedRadioValue
        }
        if (value === CUSTOM_DONATION_AMOUNT) {
          return `${value}-${radioInputValue}`
        }
      }
      return value
    },
    [selectedRadioValue, radioInputValue]
  )

  // Estrai logica di validazione custom amount
  const validateCustomAmount = useCallback(
    (value) => {
      if (value === '') {
        setError(name, {
          type: 'required',
          message: '',
        })
        return false
      } else if (value < minimumValue) {
        setError(name, {
          type: 'min',
          message: `L'importo minimo è ${minimumValue} €`,
        })
        return false
      } else {
        clearErrors(name)
        return true
      }
    },
    [minimumValue, name, setError, clearErrors]
  )

  const handleChange = useCallback(
    async (e) => {
      const value = e.target.value

      await onChange(e)
      setValue(name, value, {
        shouldDirty: true,
        shouldTouch: true,
      })

      if (value.startsWith(CUSTOM_DONATION_AMOUNT) && lastCustomValue) {
        updateRadioInputValue(lastCustomValue)
        setSelectedRadioValue(`${CUSTOM_DONATION_AMOUNT}-${lastCustomValue}`)
        setValue(name, `${CUSTOM_DONATION_AMOUNT}-${lastCustomValue}`)
        validateCustomAmount(lastCustomValue)
      } else {
        clearErrors(name)
        setSelectedRadioValue(value)
      }
    },
    [
      onChange,
      lastCustomValue,
      name,
      setValue,
      validateCustomAmount,
      clearErrors,
    ]
  )

  useEffect(() => {
    if (!currentFormValue || currentFormValue === selectedRadioValue) {
      return
    }

    setSelectedRadioValue(currentFormValue)
  }, [currentFormValue, selectedRadioValue])

  const handleKeyDown = useCallback(
    (value) => (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        const customValue = addCustomValue(value)
        handleChange({
          target: {
            value: customValue,
          },
        })
        setValue(name, customValue)
      }
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
        e.preventDefault()
        const labels = Array.from(
          e.currentTarget.parentNode.querySelectorAll('label')
        )
        const index = labels.findIndex((label) => label === e.currentTarget)
        const prevIndex = (index - 1 + labels.length) % labels.length
        labels[prevIndex].focus()
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
        e.preventDefault()
        const labels = Array.from(
          e.currentTarget.parentNode.querySelectorAll('label')
        )
        const index = labels.findIndex((label) => label === e.currentTarget)
        const nextIndex = (index + 1) % labels.length
        labels[nextIndex].focus()
      }
    },
    [addCustomValue, handleChange, name, setValue]
  )

  const handleCustomInputChange = useCallback(
    (e) => {
      const isValid = !isNaN(e.target.value) || e.target.value === ''
      if (!isValid) return

      const value = e.target.value === '' ? '' : Number(e.target.value)
      updateRadioInputValue(value)
      setLastCustomValue(value)

      if (validateCustomAmount(value)) {
        setValue(name, `${CUSTOM_DONATION_AMOUNT}-${value}`)
        setSelectedRadioValue(`${CUSTOM_DONATION_AMOUNT}-${value}`)
      }
    },
    [validateCustomAmount, name, setValue]
  )

  const handleCustomInputKeyDown = useCallback(
    (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        const _value = e.target.value
        const isValid = !isNaN(_value) && _value > 0 && _value !== null
        if (!isValid) return
        setValue(name, `${CUSTOM_DONATION_AMOUNT}-${_value}`)
        setSelectedRadioValue(`${CUSTOM_DONATION_AMOUNT}-${_value}`)
        updateRadioInputValue(_value)
      }
    },
    [name, setValue]
  )

  useEffect(() => {
    if (
      selectedRadioValue === `${CUSTOM_DONATION_AMOUNT}-` ||
      selectedRadioValue === CUSTOM_DONATION_AMOUNT
    ) {
      setError(name, {
        type: 'required',
        message: '',
      })
    } else {
      clearErrors(name)
    }
  }, [selectedRadioValue, name, setError, clearErrors])

  useEffect(() => {
    if (
      selectedRadioValue.startsWith(`${CUSTOM_DONATION_AMOUNT}-`) &&
      radioInputValue
    ) {
      validateCustomAmount(Number(radioInputValue))
    }
  }, [minimumValue, selectedRadioValue, radioInputValue, validateCustomAmount])

  if (radioOptions.length === 0) {
    return null
  }

  return (
    <div
      id={name}
      role='radiogroup'
      aria-labelledby={`${name}-label`}
      className={classNames.radioGroup}
      data-options={radioOptions.length}
      data-variant={name}
      data-current-option={selectedRadioValue.split('-')[0]}
    >
      {radioOptions.map(
        ({ value, label, subLabel, customPlaceholder }, index) => (
          <Fragment key={value}>
            <label
              id={`${value}-label`}
              tabIndex={index === 0 ? 0 : -1}
              className={`${classNames.radioOption} ${
                selectedRadioValue.startsWith('custom') &&
                value.startsWith('custom')
                  ? 'custom'
                  : ''
              }`}
              onKeyDown={handleKeyDown(value)}
            >
              <input
                ref={ref}
                type={isHidden ? 'hidden' : 'radio'}
                aria-labelledby={`${value}-label`}
                name={name}
                value={String(addCustomValue(value))}
                checked={isRadioButtonChecked(value, index)}
                onChange={handleChange}
              />
              <span className={classNames.radioOptionLabel}>{label}</span>
              {subLabel && (
                <span className={classNames.radioOptionSubLabel}>
                  {subLabel}
                </span>
              )}
            </label>
            {isRadioButtonChecked(value) &&
              value.includes(CUSTOM_DONATION_AMOUNT) && (
                <div className='radio-option_custom-wrapper'>
                  <input
                    className={`radio-option_custom-radio ${
                      radioInputValue || selectedRadioValue.startsWith('custom')
                        ? 'active'
                        : ''
                    }`}
                    autoFocus
                    type={isHidden ? 'hidden' : customOptionType}
                    pattern='[0-9]*'
                    min={1}
                    step={0.5}
                    value={radioInputValue}
                    placeholder={customPlaceholder || 'Importo'}
                    aria-label='Importo personalizzato'
                    onChange={handleCustomInputChange}
                    onKeyDown={handleCustomInputKeyDown}
                  />
                  <span
                    className={`radio-option_euro ${
                      radioInputValue || selectedRadioValue.startsWith('custom')
                        ? 'active'
                        : ''
                    }`}
                  >
                    €
                  </span>
                </div>
              )}
          </Fragment>
        )
      )}
    </div>
  )
}

function getRadioOptions(
  radioOptions,
  radioOptionsByField,
  formFields,
  defaultValue,
  value
) {
  const hasCustomValue =
    value?.startsWith(`${KNOWN_FORM_VALUES.CUSTOM_DONATION_AMOUNT}-`) ||
    defaultValue?.startsWith(`${KNOWN_FORM_VALUES.CUSTOM_DONATION_AMOUNT}-`) ||
    false

  if (radioOptionsByField === false) {
    return [radioOptions, value || defaultValue]
  }

  const frequencyOptions = radioOptionsByField[DONATION_FREQUENCY]
  if (!frequencyOptions) {
    return [radioOptions, value || defaultValue]
  }

  const frequency = formFields[DONATION_FREQUENCY]
  if (!frequency) {
    return [radioOptions, value || defaultValue]
  }

  if (frequency !== ONE_TIME_DONATION_FREQUENCY) {
    const frequencyOptionsForType = frequencyOptions[frequency]

    if (Array.isArray(frequencyOptionsForType)) {
      const firstOption = frequencyOptionsForType[0]
      const newDefaultValue =
        defaultValue !== firstOption.value && !hasCustomValue
          ? firstOption.value
          : defaultValue
      return [frequencyOptionsForType, newDefaultValue]
    }

    const recurrenceDonationType =
      formFields[DONATION_PAYMENT_METHOD_ON_RECURRENCE_PAYMENT_TYPE] ||
      GENERIC_DONATION_TYPE
    const options = frequencyOptionsForType[recurrenceDonationType]

    if (!options) {
      return [radioOptions, value || defaultValue]
    }

    const firstOption = options[0]
    const newDefaultValue =
      defaultValue !== firstOption.value && !hasCustomValue
        ? firstOption.value
        : defaultValue
    return [options, newDefaultValue]
  }

  const oneTimeOptions = frequencyOptions[ONE_TIME_DONATION_FREQUENCY]
  const donationType = formFields[DONATION_TYPE]
  if (!donationType) {
    return [radioOptions, value || defaultValue]
  }

  const options = oneTimeOptions[donationType]
  const firstOption = options[0]
  const newDefaultValue =
    defaultValue !== firstOption.value && !hasCustomValue
      ? firstOption.value
      : defaultValue
  return [options, newDefaultValue]
}

export default RadioGroup
